/**
 * AnimationController
 * 
 * Wraps GSAP for React integration, providing animation presets and scene transitions.
 * Handles cleanup and coordinate with asset preloading.
 * 
 * Supported animation presets:
 * - fadeIn: opacity 0 → 1
 * - fadeOut: opacity 1 → 0
 * - slideLeft: x: 100% → 0
 * - slideRight: x: -100% → 0
 * - slideUp: y: 100% → 0
 * - slideDown: y: -100% → 0
 */

import gsap from 'gsap';
import { AnimationPreset } from '../types';

export interface AnimationOptions {
  duration?: number;
  delay?: number;
  ease?: string;
  onComplete?: () => void;
  onStart?: () => void;
  [key: string]: any; // Allow for extra GSAP properties like scale, opacity, etc.
}

export interface SceneTransitionOptions {
  fadeOutDuration?: number;
  fadeInDuration?: number;
  onHalfway?: () => void;
  onComplete?: () => void;
}

/**
 * AnimationController class for managing GSAP animations in React
 */
export class AnimationController {
  private activeAnimations: (gsap.core.Tween | gsap.core.Timeline)[] = [];

  /**
   * Animate an element based on an AnimationPreset
   */
  animateElement(
    element: HTMLElement,
    preset: AnimationPreset
  ): gsap.core.Tween {
    const { type, duration, delay = 0 } = preset;
    
    let animation: gsap.core.Tween;

    switch (type) {
      case 'fadeIn':
        animation = this.fadeIn(element, { duration, delay });
        break;
      case 'fadeOut':
        animation = this.fadeOut(element, { duration, delay });
        break;
      case 'slideLeft':
        animation = this.slideLeft(element, { duration, delay });
        break;
      case 'slideRight':
        animation = this.slideRight(element, { duration, delay });
        break;
      case 'slideUp':
        animation = this.slideUp(element, { duration, delay });
        break;
      case 'slideDown':
        animation = this.slideDown(element, { duration, delay });
        break;
      default:
        throw new Error(`Unknown animation type: ${type}`);
    }

    return animation;
  }

  /**
   * Fade in: opacity 0 → 1
   */
  fadeIn(
    element: HTMLElement,
    options: AnimationOptions = {}
  ): gsap.core.Tween {
    const { duration = 0.5, delay = 0, ease = 'power2.out', onComplete, onStart } = options;

    // Set initial state
    gsap.set(element, { opacity: 0 });

    const animation = gsap.to(element, {
      opacity: 1,
      duration,
      delay,
      ease,
      onComplete,
      onStart,
    });

    this.activeAnimations.push(animation);
    return animation;
  }

  /**
   * Fade out: opacity 1 → 0
   */
  fadeOut(
    element: HTMLElement,
    options: AnimationOptions = {}
  ): gsap.core.Tween {
    const { duration = 0.5, delay = 0, ease = 'power2.in', onComplete, onStart } = options;

    const animation = gsap.to(element, {
      opacity: 0,
      duration,
      delay,
      ease,
      onComplete,
      onStart,
    });

    this.activeAnimations.push(animation);
    return animation;
  }

  /**
   * Slide left: x: 100% → 0
   */
  slideLeft(
    element: HTMLElement,
    options: AnimationOptions = {}
  ): gsap.core.Tween {
    const { duration = 0.6, delay = 0, ease = 'power2.out', onComplete, onStart } = options;

    // Set initial state
    gsap.set(element, { x: '100%', opacity: 0 });

    const animation = gsap.to(element, {
      x: '0%',
      opacity: 1,
      duration,
      delay,
      ease,
      onComplete,
      onStart,
    });

    this.activeAnimations.push(animation);
    return animation;
  }

  /**
   * Slide right: x: -100% → 0
   */
  slideRight(
    element: HTMLElement,
    options: AnimationOptions = {}
  ): gsap.core.Tween {
    const { duration = 0.6, delay = 0, ease = 'power2.out', onComplete, onStart } = options;

    // Set initial state
    gsap.set(element, { x: '-100%', opacity: 0 });

    const animation = gsap.to(element, {
      x: '0%',
      opacity: 1,
      duration,
      delay,
      ease,
      onComplete,
      onStart,
    });

    this.activeAnimations.push(animation);
    return animation;
  }

  /**
   * Slide up: y: 100% → 0
   */
  slideUp(
    element: HTMLElement,
    options: AnimationOptions = {}
  ): gsap.core.Tween {
    const { duration = 0.6, delay = 0, ease = 'power2.out', onComplete, onStart } = options;

    // Set initial state
    gsap.set(element, { y: '100%', opacity: 0 });

    const animation = gsap.to(element, {
      y: '0%',
      opacity: 1,
      duration,
      delay,
      ease,
      onComplete,
      onStart,
    });

    this.activeAnimations.push(animation);
    return animation;
  }

  /**
   * Slide down: y: -100% → 0
   */
  slideDown(
    element: HTMLElement,
    options: AnimationOptions = {}
  ): gsap.core.Tween {
    const { duration = 0.6, delay = 0, ease = 'power2.out', onComplete, onStart } = options;

    // Set initial state
    gsap.set(element, { y: '-100%', opacity: 0 });

    const animation = gsap.to(element, {
      y: '0%',
      opacity: 1,
      duration,
      delay,
      ease,
      onComplete,
      onStart,
    });

    this.activeAnimations.push(animation);
    return animation;
  }

  /**
   * Scene transition: fade out current scene, then fade in new scene
   */
  transitionScene(
    currentSceneElement: HTMLElement | null,
    newSceneElement: HTMLElement,
    options: SceneTransitionOptions = {}
  ): Promise<void> {
    const {
      fadeOutDuration = 0.3,
      fadeInDuration = 0.3,
      onHalfway,
      onComplete,
    } = options;

    return new Promise((resolve) => {
      // If there's a current scene, fade it out first
      if (currentSceneElement) {
        const fadeOutAnim = gsap.to(currentSceneElement, {
          opacity: 0,
          duration: fadeOutDuration,
          ease: 'power2.in',
          onComplete: () => {
            // Call halfway callback (e.g., to update scene content)
            onHalfway?.();

            // Fade in new scene
            gsap.set(newSceneElement, { opacity: 0 });
            const fadeInAnim = gsap.to(newSceneElement, {
              opacity: 1,
              duration: fadeInDuration,
              ease: 'power2.out',
              onComplete: () => {
                onComplete?.();
                resolve();
              },
            });
            this.activeAnimations.push(fadeInAnim);
          },
        });
        this.activeAnimations.push(fadeOutAnim);
      } else {
        // No current scene, just fade in the new one
        onHalfway?.();
        gsap.set(newSceneElement, { opacity: 0 });
        const fadeInAnim = gsap.to(newSceneElement, {
          opacity: 1,
          duration: fadeInDuration,
          ease: 'power2.out',
          onComplete: () => {
            onComplete?.();
            resolve();
          },
        });
        this.activeAnimations.push(fadeInAnim);
      }
    });
  }

  /**
   * Crossfade between two images within a container
   * Expects the container to have two image elements
   */
  crossfadeMood(
    fromElement: HTMLElement,
    toElement: HTMLElement,
    options: AnimationOptions = {}
  ): Promise<void> {
    const { duration = 0.6, ease = 'power1.inOut', onComplete, onStart } = options;

    return new Promise((resolve) => {
      const timeline = gsap.timeline({
        onStart: () => {
          onStart?.();
        },
        onComplete: () => {
          onComplete?.();
          resolve();
        },
      });

      // Ensure initial states
      gsap.set(toElement, { opacity: 0 });
      gsap.set(fromElement, { opacity: 1 });

      // Rule: "Quand un character change d'émotion, on fadeOut / fadeIn"
      // We perform a true crossfade
      timeline.to(fromElement, {
        opacity: 0,
        duration,
        ease,
      }, 0);

      timeline.to(toElement, {
        opacity: 1,
        duration,
        ease,
      }, 0);

      this.activeAnimations.push(timeline);
    });
  }

  /**
   * Kill a specific animation
   */
  killAnimation(animation: gsap.core.Tween | gsap.core.Timeline): void {
    animation.kill();
    this.activeAnimations = this.activeAnimations.filter(a => a !== animation);
  }

  /**
   * Kill all active animations and clean up
   * Should be called on component unmount
   */
  killAll(): void {
    this.activeAnimations.forEach(animation => animation.kill());
    this.activeAnimations = [];
  }

  /**
   * Get the number of active animations (useful for debugging)
   */
  getActiveAnimationCount(): number {
    return this.activeAnimations.length;
  }

  /**
   * Check if any animations are currently running
   */
  hasActiveAnimations(): boolean {
    return this.activeAnimations.some(animation => animation.isActive());
  }

  /**
   * Animate an element to a specific target position
   * Returns a Promise that resolves when the animation is complete
   */
  animateToPosition(
    element: HTMLElement,
    targetX: number,
    targetY: number,
    options: AnimationOptions = {}
  ): Promise<void> {
    const { duration = 1.5, delay = 0, ease = 'power2.inOut', onComplete, onStart, ...rest } = options;

    return new Promise((resolve) => {
      const animation = gsap.to(element, {
        x: targetX,
        y: targetY,
        ...rest,
        duration,
        delay,
        ease,
        onStart: () => {
          onStart?.();
        },
        onComplete: () => {
          onComplete?.();
          resolve();
        },
      });

      this.activeAnimations.push(animation);
    });
  }
}

/**
 * Create a new AnimationController instance
 */
export function createAnimationController(): AnimationController {
  return new AnimationController();
}

/**
 * Utility function to animate an element with a preset
 * Useful for one-off animations without managing a controller instance
 */
export function animateWithPreset(
  element: HTMLElement,
  preset: AnimationPreset
): gsap.core.Tween {
  const controller = new AnimationController();
  return controller.animateElement(element, preset);
}

